import React from "react";
import { Label } from "../ui/label";
import { Slider } from "../ui/slider";
import { Switch } from "../ui/switch";
import { SPAZI, StepHeading } from "../integrations/brand";

/**
 * I mattoni delle pagine di impostazioni (Widget, Impostazioni, le app):
 * testi #020617 / #64748B, scelte come in Apps & Integrazioni. Le distanze
 * sono le regole di stile di brand.jsx (SPAZI).
 */

/**
 * Un gruppo di impostazioni come i passaggi dell'onboarding: titolo con il
 * numero nel cerchio e una riga di spiegazione, poi i campi, senza card
 * intorno. Il numero lo mette Passi; da solo il gruppo e' il passo 1.
 * fatto: il numero diventa una spunta; facoltativo: lo dice accanto al titolo;
 * className: la disposizione dei campi (di norma uno sotto l'altro).
 */
export function Gruppo({ numero = 1, titolo, descrizione, azioni, fatto, facoltativo, className = "space-y-5", children }) {
  return (
    <section className={SPAZI.titoloCampi}>
      {titolo ? (
        <div className="flex items-center justify-between gap-3">
          <StepHeading number={numero} done={fatto} optional={facoltativo} title={titolo} subtitle={descrizione} />
          {azioni}
        </div>
      ) : null}
      <div className={className}>{children}</div>
    </section>
  );
}

/** I gruppi di una scheda, numerati in ordine (quelli nascosti non contano). */
export function Passi({ children }) {
  let numero = 0;
  return (
    <div className={SPAZI.traPassi}>
      {React.Children.map(children, (figlio) =>
        React.isValidElement(figlio) ? React.cloneElement(figlio, { numero: ++numero }) : figlio
      )}
    </div>
  );
}

/**
 * Un campo con etichetta e, sotto, una riga di aiuto. errore: prende il posto
 * dell'aiuto, nello stesso punto, in rosso.
 */
export function Campo({ id, etichetta, aiuto, valore, errore, children }) {
  return (
    <div className="space-y-1.5">
      {etichetta ? (
        <div className="flex items-baseline justify-between gap-3">
          <Label htmlFor={id} className="text-xs font-medium text-[#334155]">
            {etichetta}
          </Label>
          {valore !== undefined ? <span className="text-xs tabular-nums text-[#64748B]">{valore}</span> : null}
        </div>
      ) : null}
      {children}
      {errore ? (
        <p id={id ? `${id}-errore` : undefined} className="text-xs leading-5 text-[#B91C1C]">
          {errore}
        </p>
      ) : aiuto ? (
        <p className="text-xs leading-5 text-[#64748B]">{aiuto}</p>
      ) : null}
    </div>
  );
}

/** Una riga con un interruttore a destra. */
export function RigaInterruttore({ id, etichetta, aiuto, attivo, onCambia, disabilitato }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <Label htmlFor={id} className="block text-sm font-medium leading-5 text-[#18181B]">
          {etichetta}
        </Label>
        {aiuto ? <p className="text-xs leading-4 text-[#64748B]">{aiuto}</p> : null}
      </div>
      <Switch id={id} checked={Boolean(attivo)} onCheckedChange={onCambia} disabled={disabilitato} />
    </div>
  );
}

/** Un cursore con il valore scritto accanto all'etichetta. */
export function Cursore({ etichetta, aiuto, valore, formato = (v) => v, min, max, step = 1, onCambia }) {
  return (
    <Campo etichetta={etichetta} aiuto={aiuto} valore={formato(valore)}>
      <Slider
        value={[valore]}
        min={min}
        max={max}
        step={step}
        onValueChange={([v]) => onCambia(v)}
        aria-label={etichetta}
        className="py-1"
      />
    </Campo>
  );
}

/** Un colore: il campione cliccabile e il codice esadecimale da scrivere. */
export function Colore({ id, etichetta, valore, onCambia }) {
  return (
    <Campo id={id} etichetta={etichetta}>
      <div className="flex h-10 items-center gap-2 rounded-lg border border-black/10 bg-white px-1.5">
        <input
          type="color"
          aria-label={etichetta}
          value={valore || "#000000"}
          onChange={(e) => onCambia(e.target.value)}
          className="h-7 w-9 cursor-pointer rounded-md border-0 bg-transparent p-0"
        />
        <input
          id={id}
          value={valore || ""}
          onChange={(e) => onCambia(e.target.value)}
          spellCheck={false}
          className="min-w-0 flex-1 bg-transparent font-mono text-sm uppercase text-[#020617] outline-none"
        />
      </div>
    </Campo>
  );
}

/**
 * Scelte a schede (una sola alla volta): per decisioni che si capiscono a
 * colpo d'occhio, come chat o barra, chiaro o scuro, stretta o larga.
 */
export function Scelte({ etichetta, voci, valore, onCambia, colonne = 2, id }) {
  const griglia = { 2: "sm:grid-cols-2", 3: "sm:grid-cols-3", 4: "grid-cols-2 sm:grid-cols-4", 5: "grid-cols-2 sm:grid-cols-5" }[colonne];
  return (
    <div className="space-y-1.5">
      {etichetta ? (
        <p id={id} className="text-xs font-medium text-[#334155]">
          {etichetta}
        </p>
      ) : null}
      <div role="radiogroup" aria-labelledby={etichetta ? id : undefined} className={`grid gap-2 ${griglia}`}>
        {voci.map((voce) => {
          const scelta = voce.attiva !== undefined ? voce.attiva : voce.valore === valore;
          return (
            <button
              key={String(voce.valore)}
              type="button"
              role="radio"
              aria-checked={scelta}
              onClick={() => onCambia(voce.valore)}
              className={`rounded-[10px] border px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                scelta ? "border-[#18181B] bg-white shadow-[0_0_0_1px_#18181B]" : "border-[#E2E8F0] bg-white hover:border-[#CBD5E1] hover:bg-[#F8FAFC]"
              }`}
            >
              <span className="flex items-center gap-2">
                {voce.segno}
                <span className="text-sm font-medium text-[#020617]">{voce.etichetta}</span>
              </span>
              {voce.descrizione ? <span className="mt-0.5 block text-xs text-[#64748B]">{voce.descrizione}</span> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Il campo di testo della sezione. */
export const CAMPO_TESTO =
  "h-10 w-full rounded-lg border border-black/10 bg-white px-3 text-sm text-[#020617] outline-none placeholder:text-[#94A3B8] focus:border-[#94A3B8]";
export const AREA_TESTO =
  "block w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-sm leading-6 text-[#020617] outline-none placeholder:text-[#94A3B8] focus:border-[#94A3B8]";
